import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d50plu5pfatkmn6if7n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#F8F6F3]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-6xl text-[#2C2C2C] mb-6">
              Get in Touch
            </h1>
            <div className="flex items-center justify-center mb-6">
              <div className="h-0.5 w-32 bg-[#C9A961]"></div>
            </div>
            <p className="text-xl text-[#666666] leading-relaxed">
              Whether you're interested in speaking engagements, book club discussions,<br />
              or media inquiries, I'd love to hear from you
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm tracking-widest text-[#C9A961] uppercase mb-3"
                      >
                        Your Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 focus:border-[#8B2635] focus:outline-none bg-transparent text-[#2C2C2C] text-sm"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm tracking-widest text-[#C9A961] uppercase mb-3"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 focus:border-[#8B2635] focus:outline-none bg-transparent text-[#2C2C2C] text-sm"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="subject"
                      className="block text-sm tracking-widest text-[#C9A961] uppercase mb-3"
                    >
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 focus:border-[#8B2635] focus:outline-none bg-transparent text-[#2C2C2C] text-sm cursor-pointer"
                    >
                      <option value="">Select a subject</option>
                      <option value="speaking">Speaking Engagement</option>
                      <option value="bookclub">Book Club Discussion</option>
                      <option value="media">Media Inquiry</option>
                      <option value="general">General Question</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="mb-8">
                    <label
                      htmlFor="message"
                      className="block text-sm tracking-widest text-[#C9A961] uppercase mb-3"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 focus:border-[#8B2635] focus:outline-none bg-transparent text-[#2C2C2C] text-sm resize-none"
                      placeholder="Tell me about your inquiry..."
                    ></textarea>
                    <p className="text-xs text-[#666666] mt-2">
                      {formData.message.length}/500 characters
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                      Thank you for your message! I'll get back to you soon.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      Please fill in all fields correctly. Message must be 500 characters or less.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-[#8B2635] text-white rounded-lg hover:bg-[#6d1e29] transition-all shadow-md text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  <p className="text-center text-sm text-[#666666] mt-6">
                    Or email me directly at{' '}
                    <a
                      href="mailto:contact@edwarddumke.com"
                      className="text-[#8B2635] hover:underline"
                    >
                      contact@edwarddumke.com
                    </a>
                  </p>
                </form>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Connect Section */}
              <div className="bg-[#F8F6F3] rounded-2xl p-8">
                <h3 className="text-sm tracking-widest text-[#C9A961] uppercase mb-6">
                  Connect
                </h3>
                <div className="space-y-4">
                  <a
                    href="https://www.amazon.com/stores/Edward-J.-Dumke/author/B001KDIDRS"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-4 p-4 bg-white rounded-lg hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-[#8B2635] text-[#8B2635]">
                      <i className="ri-amazon-line text-xl"></i>
                    </div>
                    <span className="text-[#2C2C2C] font-medium">Amazon Author Page</span>
                  </a>
                </div>
              </div>

              {/* Newsletter Section */}
              <div className="bg-[#F8F6F3] rounded-2xl p-8">
                <h3 className="text-sm tracking-widest text-[#C9A961] uppercase mb-4">
                  Newsletter
                </h3>
                <p className="text-sm text-[#666666] mb-6">
                  Subscribe to receive updates on new releases and exclusive content
                </p>
                <div className="space-y-3">
                  <input
                    type="email"
                    placeholder="Your email"
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#8B2635] focus:outline-none text-sm"
                  />
                  <button className="w-full py-3 bg-[#C9A961] text-white rounded-lg hover:bg-[#b89850] transition-all shadow-md text-sm font-medium whitespace-nowrap">
                    Subscribe
                  </button>
                </div>
              </div>

              {/* Quote Section */}
              <div className="bg-white rounded-2xl p-8 border-l-4 border-[#C9A961]">
                <p className="font-serif italic text-xl text-[#2C2C2C] leading-relaxed mb-4">
                  "The stories we tell connect us across generations and remind us of our shared humanity."
                </p>
                <p className="text-sm text-[#C9A961]">— Edward J. Dumke</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;